import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Trans, useLingui } from '@lingui/react/macro';
import { X } from 'lucide-react';

import { Alert, AlertDescription } from '../../ui/alert';

/**
 * Browsers embedded in another app. Ad traffic lands in these constantly — a Facebook ad opens in
 * Facebook's own webview — and they are a bad place to sign up: reCAPTCHA is unreliable inside
 * them, and a user who can't get an SMS code tends to retry until Firebase rate-limits them, then
 * start over, which is one of the ways we end up with two accounts for one person.
 */
const IN_APP_BROWSER_UA = /FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|TikTok|MicroMessenger/i;

const isInAppBrowser = () => {
  if (Capacitor.isNativePlatform()) return false;

  return IN_APP_BROWSER_UA.test(window.navigator.userAgent);
};

const isAndroid = () => /Android/i.test(window.navigator.userAgent);

/**
 * Android can hand a URL straight to Chrome with an intent link. iOS has no equivalent, so there
 * the notice can only tell people where the "open in browser" control is.
 */
const chromeIntentUrl = () => {
  const { host, pathname, search, hash } = window.location;

  return `intent://${host}${pathname}${search}${hash}#Intent;scheme=https;package=com.android.chrome;end`;
};

export const InAppBrowserNotice = () => {
  const { t } = useLingui();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !isInAppBrowser()) return null;

  return (
    <Alert className="mb-4 flex items-start gap-2">
      <AlertDescription className="flex-1 text-sm">
        {isAndroid() ? (
          <Trans>
            For a better experience, open this page in your browser.{' '}
            <a className="text-primary underline" href={chromeIntentUrl()}>
              Open in Chrome
            </a>
          </Trans>
        ) : (
          <Trans>
            For a better experience, open this page in Safari — tap the menu in the corner and choose &quot;Open in
            Safari&quot;.
          </Trans>
        )}
      </AlertDescription>
      <button type="button" aria-label={t`Dismiss`} onClick={() => setDismissed(true)}>
        <X className="w-4 h-4" />
      </button>
    </Alert>
  );
};
